import ErrorDisplay from '../../../components/ErrorDisplay';
import MailTemplateDetails from './MailTemplateDetails';
import { Box, Flex, Heading, ProgressCircle, Stack, Text } from '@chakra-ui/react';
import type { EmailTemplateDocument } from '@opanuj/shared/types';
import { mailTemplateUpdate } from '../../../operations/admin/mailTemplateUpdate';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { toaster } from '../../../components/ui/toaster';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEffect, useMemo, useState } from 'react';
import { useEmailTemplatesList } from '../../../hooks/useEmailTemplates';

const EmailTemplatesPage = () => {
  useEffect(setDocumentTitle('Szablony maili')); // eslint-disable-line react-hooks/exhaustive-deps

  const database = useDatabase();
  const [templates, loading, error] = useEmailTemplatesList();
  const [selectedId, setSelectedId] = useState<string>('');
  const [subjectDraft, setSubjectDraft] = useState('');
  const [htmlDraft, setHtmlDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const templateGroups = useMemo(() => {
    const precedenceOrder: Record<string, number> = {
      'new-reservation-client': 1,
      'reservation-approved-client': 2,
      'lesson-cancelled-client': 3,
      'lesson-reschedule-accepted-client': 4,
      'lesson-reschedule-rejected-client': 5,
      'client-panel-invitation': 6,
      'new-lead-admin': 1,
      'new-reservation-admin': 2,
      'lesson-cancelled-admin': 3,
      'lesson-reschedule-proposal-admin': 4,
    };

    const groups = {
      client: [] as EmailTemplateDocument[],
      admin: [] as EmailTemplateDocument[],
      other: [] as EmailTemplateDocument[],
    };

    for (const template of templates) {
      const normalizedId = template.id.toLowerCase();
      if (normalizedId.endsWith('-client') || normalizedId.startsWith('client-')) {
        groups.client.push(template);
      } else if (normalizedId.endsWith('-admin')) {
        groups.admin.push(template);
      } else {
        groups.other.push(template);
      }
    }

    const sortByPrecedence = (items: EmailTemplateDocument[]) =>
      [...items].sort((a, b) => {
        const aOrder = precedenceOrder[a.id] ?? Number.MAX_SAFE_INTEGER;
        const bOrder = precedenceOrder[b.id] ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });

    return [
      { key: 'client', title: 'Maile do klientów', items: sortByPrecedence(groups.client) },
      { key: 'admin', title: 'Maile administracyjne', items: sortByPrecedence(groups.admin) },
      { key: 'other', title: 'Pozostałe', items: [...groups.other].sort((a, b) => a.id.localeCompare(b.id)) },
    ];
  }, [templates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedId) ?? templates[0] ?? null,
    [selectedId, templates],
  );

  useEffect(() => {
    if (!templates.length) {
      setSelectedId('');
      setSubjectDraft('');
      setHtmlDraft('');
      return;
    }

    if (!selectedId || !templates.some((template) => template.id === selectedId)) {
      setSelectedId(templates[0].id);
    }
  }, [selectedId, templates]);

  useEffect(() => {
    if (!selectedTemplate) {
      setSubjectDraft('');
      setHtmlDraft('');
      return;
    }

    setSubjectDraft(selectedTemplate.subject ?? '');
    setHtmlDraft(selectedTemplate.html ?? '');
  }, [selectedTemplate]);

  const handleSave = async () => {
    if (!selectedTemplate) {
      return;
    }

    setSaving(true);
    try {
      await mailTemplateUpdate(database, {
        id: selectedTemplate.id,
        subject: subjectDraft,
        html: htmlDraft,
        attachments: selectedTemplate.attachments,
      });

      toaster.create({
        title: 'Zmiany w szablonie zapisane',
        type: 'success',
        duration: 2400,
      });
    } catch (saveError) {
      console.error('Failed to update mail template', saveError);
      toaster.create({
        title: 'Nie udało się zapisać zmian',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box py={12}>
        <ProgressCircle.Root value={null}>
          <ProgressCircle.Circle>
            <ProgressCircle.Track />
            <ProgressCircle.Range stroke="orange.400" />
          </ProgressCircle.Circle>
        </ProgressCircle.Root>
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <Flex h="full">
      <Box w={{ base: '100%', lg: '320px' }} borderRightWidth="1px" borderColor="gray.200" >
        <Stack gap={0} overflowY="auto">
          {templates.length === 0 ? (
            <Box px={4} py={6} color="gray.500">Brak szablonów.</Box>
          ) : (
            templateGroups.map((group) => {
              if (group.items.length === 0) {
                return null;
              }

              return (
                <Box key={group.key}>
                  <Box px={4} py={2} bg="gray.100" borderBottomWidth="1px" borderColor="gray.200">
                    <Heading size="sm">{group.title}</Heading>
                  </Box>

                  {group.items.map((template) => {
                    const isSelected = template.id === selectedTemplate?.id;
                    return (
                      <Box
                        key={template.id}
                        px={4}
                        py={3}
                        borderBottomWidth="1px"
                        borderColor='gray.200'
                        bg={isSelected ? 'orange.200' : 'transparent'}
                        cursor="pointer"
                        onClick={() => setSelectedId(template.id)}
                        _hover={{ bg: isSelected ? 'orange.200' : 'orange.50' }}
                      >
                        <Text fontWeight={isSelected ? 'bold' : 'medium'}>{template.subject || 'Bez tematu'}</Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>{template.id}</Text>
                      </Box>
                    );
                  })}
                </Box>
              );
            })
          )}
        </Stack>
      </Box>

      <Box flex={1} minW={0} bg="white">
        <MailTemplateDetails
          template={selectedTemplate}
          subjectDraft={subjectDraft}
          htmlDraft={htmlDraft}
          saving={saving}
          onSubjectChange={setSubjectDraft}
          onHtmlChange={setHtmlDraft}
          onSave={handleSave}
        />
      </Box>
    </Flex>
  );
};

export default EmailTemplatesPage;
